import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Clock, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DEFAULT_FREE_UNTIL_LAUNCH_MESSAGE = (
  "هذا المطعم يستخدم خِفّه مجانًا حتى الإطلاق الرسمي. "
  + "سيتم إشعاركم مسبقًا قبل بدء الاشتراك المدفوع."
);

/**
 * Owner-facing banner that reflects the account expiration status.
 * Source of truth is /api/account/me (backend-computed), never a client-side countdown.
 */
export default function AccountBanner() {
  const { user } = useAuth();
  const [acc, setAcc] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/account/me").then(r => alive && setAcc(r.data)).catch(() => {});
      api.get("/notifications").then(r => alive && setNotifs(r.data || [])).catch(() => {});
    };
    load();
    // Refresh once a minute — real time comes from the backend, not a JS timer.
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Don't render at all until we have data AND either an expiry snapshot or notifs.
  if (!acc) return null;
  const days = acc.days_remaining;
  const isExpired = acc.is_expired;
  const isFreeUntilLaunch = acc.account_status === "FREE_UNTIL_LAUNCH";
  const showFreeUntilLaunch = isFreeUntilLaunch && user?.role === "OWNER";
  const hasExpirySnapshot = acc.account_expires_at && (days !== null && days !== undefined);
  const unread = notifs.filter(n => !n.read).length;
  if (!hasExpirySnapshot && notifs.length === 0 && !showFreeUntilLaunch) return null;

  let tone = "bg-white border-orange-100 text-gray-800";
  let icon = <Clock size={16} className="text-orange-600" />;
  let msg = "";
  if (showFreeUntilLaunch) {
    tone = "bg-teal-50 border-teal-200 text-teal-900";
    icon = <Clock size={16} className="text-teal-700" />;
    msg = acc.free_until_launch_message || DEFAULT_FREE_UNTIL_LAUNCH_MESSAGE;
  } else if (isExpired) {
    tone = "bg-red-50 border-red-200 text-red-800";
    icon = <AlertTriangle size={16} className="text-red-600" />;
    msg = "انتهت صلاحية حسابك — يرجى التواصل مع الإدارة لتجديد الاشتراك.";
  } else if (hasExpirySnapshot) {
    if (days <= 3) {
      tone = "bg-red-50 border-red-200 text-red-800";
      icon = <AlertTriangle size={16} className="text-red-600" />;
    } else if (days <= 7) {
      tone = "bg-amber-50 border-amber-200 text-amber-800";
    }
    if (acc.account_status === "TRIAL") {
      msg = days === 0
        ? "ينتهي الاشتراك التجريبي اليوم"
        : days === 1
          ? "متبقي يوم واحد على انتهاء الفترة التجريبية"
          : days === 2
            ? "متبقي يومان على انتهاء الفترة التجريبية"
            : `متبقي ${days} ${days <= 10 ? "أيام" : "يومًا"} على انتهاء الفترة التجريبية`;
    } else {
      msg = days === 0
        ? "ينتهي اشتراكك اليوم"
        : days === 1
          ? "ينتهي اشتراكك خلال يوم"
          : days === 2
            ? "ينتهي اشتراكك خلال يومين"
            : `متبقي ${days} ${days <= 10 ? "أيام" : "يومًا"} على انتهاء اشتراكك`;
    }
  }

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); setNotifs(x => x.map(n => n.id === id ? { ...n, read: true } : n)); } catch {}
  };

  return (
    <div className={`relative rounded-2xl border p-3 mb-3 flex items-center gap-2 ${tone}`} data-testid="account-banner">
      {icon}
      <div className="flex-1 text-sm font-bold" data-testid="account-banner-msg">{msg || "التنبيهات"}</div>
      {notifs.length > 0 && (
        <button
          onClick={() => setShowNotifs(s => !s)}
          data-testid="notifs-toggle"
          className="relative w-9 h-9 rounded-xl bg-white border border-orange-200 grid place-items-center">
          <Bell size={16} className="text-orange-600" />
          {unread > 0 && (
            <span className="absolute -top-1 -end-1 min-w-[16px] h-4 rounded-full bg-red-600 text-white text-[10px] px-1 grid place-items-center num-badge" data-testid="notifs-unread">{unread}</span>
          )}
        </button>
      )}
      {showNotifs && (
        <div className="absolute z-30 top-[calc(100%+6px)] end-2 w-80 max-w-[92vw] bg-white rounded-2xl border border-orange-100 shadow-lg p-2 space-y-1" data-testid="notifs-list">
          {notifs.length === 0 && <div className="text-xs text-gray-500 p-3">لا توجد تنبيهات</div>}
          {notifs.map(n => (
            <div key={n.id} onClick={() => markRead(n.id)}
              className={`rounded-xl p-2 text-xs cursor-pointer ${n.read ? "bg-gray-50 text-gray-500" : "bg-orange-50 text-orange-800 font-bold"}`}
              data-testid={`notif-${n.id}`}>
              {n.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
